import type { AnswerValue } from "@/commonTypes/answer";
import {
  QuestionType,
  type Question,
  type MultipleChoiceQuestion,
} from "@/commonTypes/question";
import { getQuizDurationSeconds } from "@/utils/sessionStorage";

/**
 * Sends events to Google Analytics via data layer.
 * @param payload - The event payload to send
 */
export const sendAnalyticsEvent = (payload: Record<string, unknown>): void => {
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }
};

// ─── v3 Pro Analytics ────────────────────────────────────────────────────────

export type SleepQuizQuestionType =
  | "single_choice"
  | "multiple_choice"
  | "text_field"
  | "dropdown";

const SENSITIVE_QUESTION_IDS = new Set(["user-name", "email", "phone"]);

/**
 * Maps the internal QuestionType enum (and allowMultipleAnswers flag) to the
 * spec's questionType string. Returns null for question types that do not
 * produce answer events (TRANSITION, SUBMIT).
 *
 * TODO: Confirm DATE_PICKER maps to 'dropdown' once step numbering is finalised.
 */
export const mapToSleepQuizQuestionType = (
  questionType: QuestionType,
  allowMultipleAnswers?: boolean,
): SleepQuizQuestionType | null => {
  switch (questionType) {
    case QuestionType.MULTIPLE_CHOICE:
      return allowMultipleAnswers ? "multiple_choice" : "single_choice";
    case QuestionType.TEXT:
      return "text_field";
    case QuestionType.DATE_PICKER:
      return "dropdown";
    default:
      return null;
  }
};

/**
 * Converts an AnswerValue to the pipe-separated string format expected by the
 * spec. Arrays become 'a|b|c', Records become 'v1|v2', primitives are cast
 * to string.
 */
export const formatAnswerForAnalytics = (answer: AnswerValue): string => {
  if (Array.isArray(answer)) {
    return answer.join("|");
  }
  if (typeof answer === "object" && answer !== null) {
    return Object.values(answer).join("|");
  }
  return String(answer);
};

/**
 * Helper function to convert experiment assignments to comma-separated string format.
 * @param experimentAssignments - Map of experimentId to variant name
 * @returns Comma-separated string "experimentId:variant,experimentId:variant" or undefined if empty/null
 */
export const formatExperimentVariants = (
  experimentAssignments: Record<string, string> | null | undefined,
): string | undefined => {
  if (
    !experimentAssignments ||
    Object.keys(experimentAssignments).length === 0
  ) {
    return undefined;
  }
  return Object.entries(experimentAssignments)
    .map(([expId, variant]) => `${expId}:${variant}`)
    .join(",");
};

/**
 * Persistent page-context push (no `event` key).
 * GTM picks this up as a dataLayer page context update.
 * Fire on every page/screen transition.
 *
 * @param experimentVariant - Optional comma-separated list of active experiment/tag
 *   variants (e.g. "experimentId:variant,experimentId:variant"). 
 *   Reserved for future A/B tessting; omitted from the push when not provided.
 */
export const pushPageContext = (
  pageName: string,
  pageVariant: string,
  experimentVariant?: string,
): void => {
  const payload: Record<string, unknown> = {
    pageArea: "sleep_assessment_pro",
    pageName,
    pageVariant,
  };
  if (experimentVariant !== undefined)
    payload.experimentVariant = experimentVariant;
  sendAnalyticsEvent(payload);
};

/**
 * sleep_quiz event — fires on 'start', each question page view, and 'finish'.
 *
 * @param questionId - The question identifier for page-view events. Pass
 *   `undefined` for the 'start' and 'finish' sentinels (no question active).
 * @param percentComplete - Integer 0–100 representing progress through the
 *   visible question set. Pass 0 for 'start' and 100 for 'finish'.
 */
export const pushSleepQuizEvent = (
  questionId: string | undefined,
  percentComplete: number,
  pageVariant: string,
  questionType?: string,
): void => {
  sendAnalyticsEvent({
    event: "sleep_quiz",
    questionId,
    percentComplete,
    pageVariant,
    questionType: questionType ?? undefined,
    // Clear answer fields from prior sleep_quiz_answer
    question: undefined,
    answer: undefined,
    // Clear back-nav fields from prior sleep_quiz_back
    toQuestionId: undefined,
    toPercentComplete: undefined,
    // Clear form fields from prior form_interaction
    formAction: undefined,
    formResponse: undefined,
    formStatus: undefined,
    formName: undefined,
    fieldsList: undefined,
    saDuration: undefined,
  });
};

/** sleep_quiz_answer event — fires when the user clicks Next on an answerable question. */
export const pushSleepQuizAnswer = (
  questionId: string,
  percentComplete: number,
  question: string,
  answer: string,
  questionType: SleepQuizQuestionType,
  pageVariant: string,
): void => {
  sendAnalyticsEvent({
    event: "sleep_quiz_answer",
    questionId,
    percentComplete,
    question,
    answer,
    questionType,
    pageVariant,
    // Clear back-nav fields from prior sleep_quiz_back
    toQuestionId: undefined,
    toPercentComplete: undefined,
    // Clear form fields from prior form_interaction
    formAction: undefined,
    formResponse: undefined,
    formStatus: undefined,
    formName: undefined,
    fieldsList: undefined,
  });
};

/** sleep_quiz_back event — fires when the user clicks the Back button. */
export const pushSleepQuizBack = (
  questionId: string,
  toQuestionId: string,
  percentComplete: number,
  toPercentComplete: number,
  pageVariant: string,
): void => {
  sendAnalyticsEvent({
    event: "sleep_quiz_back",
    questionId,
    toQuestionId,
    percentComplete,
    toPercentComplete,
    pageVariant,
    // Clear answer fields from prior sleep_quiz_answer
    question: undefined,
    answer: undefined,
    questionType: undefined,
    // Clear form fields from prior form_interaction
    formAction: undefined,
    formResponse: undefined,
    formStatus: undefined,
    formName: undefined,
    fieldsList: undefined,
  });
};

/** sleep_quiz_skip event — fires when the user clicks the Skip button. */
export const pushSleepQuizSkip = (
  questionId: string,
  toQuestionId: string,
  percentComplete: number,
  toPercentComplete: number,
  pageVariant: string,
): void => {
  sendAnalyticsEvent({
    event: "sleep_quiz_skip",
    questionId,
    toQuestionId,
    percentComplete,
    toPercentComplete,
    pageVariant,
    // Clear answer fields from prior sleep_quiz_answer
    question: undefined,
    answer: undefined,
    questionType: undefined,
    // Clear form fields from prior form_interaction
    formAction: undefined,
    formResponse: undefined,
    formStatus: undefined,
    formName: undefined,
    fieldsList: undefined,
  });
};

/**
 * sleep_quiz_exit event — fires when the user abandons mid-quiz.
 * Uses the visibilitychange API (document hidden) as the trigger.
 * Only fires when the quiz is in progress (after 'start', before 'finish').
 */
export const pushSleepQuizExit = (
  questionId: string | undefined,
  percentComplete: number,
  pageVariant: string,
  saDuration: string,
): void => {
  sendAnalyticsEvent({
    event: "sleep_quiz_exit",
    questionId,
    percentComplete,
    saDuration,
    pageVariant,
    // Clear stale fields
    question: undefined,
    answer: undefined,
    questionType: undefined,
    toQuestionId: undefined,
    toPercentComplete: undefined,
    formAction: undefined,
    formResponse: undefined,
    formStatus: undefined,
    formName: undefined,
    fieldsList: undefined,
  });
};

/**
 * Combined result-page context push + sleep_quiz_finish event.
 * Per spec section 7.1, pageName, pageVariant, event, and action are all
 * pushed in the same dataLayer object.
 */
export const pushSleepQuizFinish = (
  pageName: "sa-finish-likely" | "sa-finish-unlikely",
  action: "likely" | "unlikely",
  pageVariant: string,
  saDuration: string,
): void => {
  sendAnalyticsEvent({
    pageArea: "sleep_assessment_pro",
    pageName,
    pageVariant,
    event: "sleep_quiz_finish",
    action,
    saDuration,
    // Explicitly clear answer fields that persist in dataLayer from the last
    // sleep_quiz_answer push and the legacy sap-quiz events.
    question: undefined,
    answer: undefined,
    questionType: undefined,
    questionId: undefined,
    percentComplete: undefined,
    toQuestionId: undefined,
    toPercentComplete: undefined,
    formAction: undefined,
    formResponse: undefined,
    formStatus: undefined,
    formName: undefined,
    fieldsList: undefined,
    answerId: undefined,
    stage: undefined,
  });
};

// ─── High-level analytics calls ─────────────────────────────────────────────
// Components call these; all derivation logic stays here.

function computePercentComplete(
  questionId: string,
  visibleQuestions: Question[],
): number {
  const idx =
    visibleQuestions.findIndex((q) => q.questionId === questionId) + 1;
  return visibleQuestions.length > 0
    ? Math.round((idx / visibleQuestions.length) * 100)
    : 0;
}

/** Fire step view event. Skips SUBMIT questions (finish event handles those). */
export function fireStepView(
  question: Question,
  visibleQuestions: Question[],
  pageVariant: string,
): void {
  if (question.questionType === QuestionType.SUBMIT) return;
  const percentComplete = computePercentComplete(
    question.questionId,
    visibleQuestions,
  );
  const questionType =
    question.questionType === QuestionType.TRANSITION
      ? "transition"
      : undefined;
  pushSleepQuizEvent(
    question.questionId,
    percentComplete,
    pageVariant,
    questionType,
  );
}

/** Fire sa-quiz page context + sleep_quiz start event. */
export function fireQuizStarted(pageVariant: string, experimentVariant?: string): void {
  pushPageContext("sa-quiz", pageVariant, experimentVariant);
  pushSleepQuizEvent(undefined, 0, pageVariant);
}

/** Fire sleep_quiz_answer for the current question. No-ops for non-answerable types. */
export function fireAnswerEvent(
  question: Question,
  answer: AnswerValue,
  visibleQuestions: Question[],
  pageVariant: string,
  t: (key: string) => string,
): void {
  const analyticsType = mapToSleepQuizQuestionType(
    question.questionType,
    question.questionType === QuestionType.MULTIPLE_CHOICE
      ? (question as MultipleChoiceQuestion).allowMultipleAnswers
      : undefined,
  );
  if (analyticsType === null) return;

  let answerText = "";
  if (!SENSITIVE_QUESTION_IDS.has(question.questionId)) {
    if (
      analyticsType === "single_choice" ||
      analyticsType === "multiple_choice"
    ) {
      const mcq = question as MultipleChoiceQuestion;
      const ids = Array.isArray(answer) ? answer : [String(answer)];
      answerText = ids
        .map((id) => {
          const opt = mcq.answers.find((a) => a.id === id);
          return opt ? t(opt.translationKey) : String(id);
        })
        .join("|");
    } else {
      answerText = formatAnswerForAnalytics(answer);
    }
  }

  const percentComplete = computePercentComplete(
    question.questionId,
    visibleQuestions,
  );
  pushSleepQuizAnswer(
    question.questionId,
    percentComplete,
    t(question.questionTranslationKey),
    answerText,
    analyticsType,
    pageVariant,
  );
}

/** Fire sleep_quiz_back. No-ops if the question is already the first visible. */
export function fireBackEvent(
  currentQuestion: Question,
  visibleQuestions: Question[],
  pageVariant: string,
): void {
  const visibleIndex = visibleQuestions.findIndex(
    (q) => q.questionId === currentQuestion.questionId,
  );
  if (visibleIndex <= 0) return;
  const toQuestion = visibleQuestions[visibleIndex - 1];
  const visibleTotal = visibleQuestions.length;
  pushSleepQuizBack(
    currentQuestion.questionId,
    toQuestion.questionId,
    Math.round(((visibleIndex + 1) / visibleTotal) * 100),
    Math.round((visibleIndex / visibleTotal) * 100),
    pageVariant,
  );
}

/** Fire sleep_quiz_skip. Computes percent from/to for the skip navigation. */
export function fireSkipEvent(
  currentQuestion: Question,
  toQuestionId: string,
  visibleQuestions: Question[],
  pageVariant: string,
): void {
  const fromPercent = computePercentComplete(
    currentQuestion.questionId,
    visibleQuestions,
  );
  const toPercent = computePercentComplete(toQuestionId, visibleQuestions);
  pushSleepQuizSkip(
    currentQuestion.questionId,
    toQuestionId,
    fromPercent,
    toPercent,
    pageVariant,
  );
}

/** Fire sleep_quiz_exit with current progress + duration. */
export function fireExitEvent(
  currentQuestion: Question | null,
  visibleQuestions: Question[],
  pageVariant: string,
): void {
  const percentComplete = currentQuestion
    ? computePercentComplete(currentQuestion.questionId, visibleQuestions)
    : 0;
  pushSleepQuizExit(
    currentQuestion?.questionId,
    percentComplete,
    pageVariant,
    getQuizDurationSeconds(),
  );
}

/** Fire the 100% sleep_quiz event when transitioning to polling. */
export function fireQuizComplete(pageVariant: string): void {
  pushSleepQuizEvent(undefined, 100, pageVariant);
}

/** Fire sleep_quiz_finish on the results page. */
export function fireQuizFinish(
  resultOutcome: "likely" | "unlikely",
  pageVariant: string,
): void {
  const pageName =
    resultOutcome === "likely" ? "sa-finish-likely" : "sa-finish-unlikely";
  pushSleepQuizFinish(
    pageName,
    resultOutcome,
    pageVariant,
    getQuizDurationSeconds(),
  );
}
